/**
 * Doctor-selectable units for the PN vitals rows (Objective section), keyed by
 * LOINC code. `display` is what the form row carries (`valueQuantity.unit`);
 * `ucum` is the token persisted through `SoapNoteDto.{temp,wt,ht,glucose}Unit`
 * and stamped on the locked Observation. The FIRST option is the default and
 * matches what the BE has always stamped (US customary / mg/dL) — vitals not
 * listed here keep their single fixed unit.
 */
export type VitalUnitOption = { display: string; ucum: string }

export const VITAL_UNIT_OPTIONS: Record<string, VitalUnitOption[]> = {
  // Body temperature
  '8310-5': [
    { display: '°F', ucum: '[degF]' },
    { display: '°C', ucum: 'Cel' },
  ],
  // Body weight
  '29463-7': [
    { display: 'lb', ucum: '[lb_av]' },
    { display: 'kg', ucum: 'kg' },
  ],
  // Body height
  '8302-2': [
    { display: 'in', ucum: '[in_i]' },
    { display: 'cm', ucum: 'cm' },
  ],
  // Blood glucose
  '2339-0': [
    { display: 'mg/dL', ucum: 'mg/dL' },
    { display: 'mmol/L', ucum: 'mmol/L' },
  ],
}

/** 41653-7 (glucometer glucose) shares 2339-0's slot, options, and unit field. */
export function canonicalVitalCode(code: string): string {
  return code === '41653-7' ? '2339-0' : code
}

/** SoapNoteDto unit field per typed vital slot (slot names double as QR linkIds). */
export const VITAL_UNIT_FIELDS: Record<string, string> = {
  temp: 'tempUnit',
  wt: 'wtUnit',
  ht: 'htUnit',
  bloodGlucose: 'glucoseUnit',
}

/** Default display unit for a vital, or undefined when it has no dropdown. */
export function defaultVitalUnit(code: string | undefined): string | undefined {
  return code ? VITAL_UNIT_OPTIONS[code]?.[0]?.display : undefined
}

/** Display → UCUM for the persisted DTO unit fields (unknown → default). */
export function vitalUnitToUcum(code: string, display: string | undefined): string | undefined {
  const options = VITAL_UNIT_OPTIONS[code]
  if (!options) return undefined
  return (options.find((o) => o.display === display) ?? options[0]).ucum
}

// Free-form spellings (AI transcripts, legacy QR values) → canonical display.
const UNIT_ALIASES: Record<string, string> = {
  f: '°F',
  '°f': '°F',
  degf: '°F',
  '[degf]': '°F',
  fahrenheit: '°F',
  c: '°C',
  '°c': '°C',
  cel: '°C',
  degc: '°C',
  celsius: '°C',
  centigrade: '°C',
  lb: 'lb',
  lbs: 'lb',
  pound: 'lb',
  pounds: 'lb',
  '[lb_av]': 'lb',
  kg: 'kg',
  kgs: 'kg',
  kilo: 'kg',
  kilos: 'kg',
  kilogram: 'kg',
  kilograms: 'kg',
  in: 'in',
  inch: 'in',
  inches: 'in',
  '[in_i]': 'in',
  cm: 'cm',
  centimeter: 'cm',
  centimeters: 'cm',
  'mg/dl': 'mg/dL',
  mgdl: 'mg/dL',
  'mmol/l': 'mmol/L',
  mmoll: 'mmol/L',
}

/**
 * Normalize a free-form/UCUM unit onto the vital's canonical display option.
 * Vitals without a dropdown (or unknown codes) pass the raw unit through;
 * unmatched spellings fall back to the vital's default option.
 */
export function normalizeVitalUnit(
  code: string | undefined,
  raw: string | undefined | null,
): string | undefined {
  const canonical = code ? canonicalVitalCode(code) : code
  if (!canonical || !VITAL_UNIT_OPTIONS[canonical]) return raw ?? undefined
  const options = VITAL_UNIT_OPTIONS[canonical]
  const mapped = UNIT_ALIASES[(raw ?? '').trim().toLowerCase()]
  if (mapped && options.some((o) => o.display === mapped)) return mapped
  return options[0].display
}

/** Saved UCUM token (QR linkId / blob field) → row display, defaulting to `fallback`. */
export function resolveSavedVitalUnit(
  loinc: string,
  raw: string | undefined,
  fallback: string,
): string {
  return raw ? (normalizeVitalUnit(loinc, raw) ?? fallback) : fallback
}
